import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ShopNav from "@/components/feature/ShopNav";
import ShopFooter from "@/components/feature/ShopFooter";
import { supabase, formatPrice } from "@/lib/supabase";

const TEXT_COLOR = "#545454";
const BORDER_SOFT = "rgba(84,84,84,0.14)";
const BORDER_MID = "rgba(84,84,84,0.22)";

interface OrderItem {
  id: number;
  product_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
}

interface OrderRecipient {
  name?: string;
  email?: string;
  phone?: string;
  country?: string;
  state?: string;
  city?: string;
  zip?: string;
  address?: string;
}

interface Order {
  id: number;
  status: string | null;
  currency: string;
  subtotal_items: number | null;
  shipping_total: number | null;
  customer_notes: string | null;
  recipient: OrderRecipient | null;
  created_at: string;
  order_items: OrderItem[];
}

export default function OrderSuccessPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const oid = Number(orderId);
        if (!oid) throw new Error("Invalid order id");
        const { data, error: e } = await supabase
          .from("order_headers")
          .select("*, order_items(*)")
          .eq("id", oid)
          .maybeSingle();
        if (cancelled) return;
        if (e) throw e;
        if (!data) throw new Error("Order not found");
        setOrder(data as unknown as Order);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : "Failed to load order";
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orderId]);

  const currency = order?.currency ?? "EUR";
  const subtotal = Number(order?.subtotal_items ?? 0);
  const shipping = Number(order?.shipping_total ?? 0);
  const total = subtotal + shipping;

  return (
    <main
      style={{
        background: "#FFFFFF",
        minHeight: "100vh",
        fontFamily: "'Inter Tight', sans-serif",
        color: TEXT_COLOR,
      }}
    >
      <ShopNav variant="light" />

      {/* Hero-style headline block */}
      <section style={{ padding: "140px 40px 40px", maxWidth: 1080, margin: "0 auto" }}>
        <div className="flex items-center" style={{ gap: 14, marginBottom: 22 }}>
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontSize: 22,
              color: "rgba(84,84,84,0.55)",
            }}
          >
            (03)
          </span>
          <span style={{ height: 1, flex: "0 0 60px", background: BORDER_MID }} />
          <span style={{ fontSize: 11, letterSpacing: "2.5px", color: "rgba(84,84,84,0.55)" }}>
            CONFIRMED · STEP 3 OF 3
          </span>
        </div>

        <motion.h1
          initial={{ opacity: 0, filter: "blur(12px)", y: 12 }}
          animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
            fontWeight: 600,
            fontSize: 96,
            lineHeight: 0.95,
            letterSpacing: "-2.4px",
            color: TEXT_COLOR,
            margin: 0,
          }}
        >
          Thank{" "}
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              fontWeight: 400,
              letterSpacing: "-3.8px",
            }}
          >
            you.
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
          style={{
            fontSize: 15,
            color: "rgba(84,84,84,0.7)",
            marginTop: 22,
            maxWidth: 520,
            lineHeight: 1.75,
          }}
        >
          Your order has been received and is being wrapped by hand in our Milano atelier.
          A confirmation has drifted to{" "}
          <span
            style={{
              fontFamily: "'Instrument Serif', serif",
              fontStyle: "italic",
              color: TEXT_COLOR,
            }}
          >
            {order?.recipient?.email || "your inbox"}
          </span>
          .
        </motion.p>
      </section>

      {loading && (
        <div
          style={{
            padding: "40px 32px 120px",
            textAlign: "center",
            color: "rgba(84,84,84,0.55)",
            letterSpacing: "2px",
            fontSize: 12,
          }}
        >
          LOADING ORDER…
        </div>
      )}

      {error && (
        <div style={{ padding: "40px 32px 120px", textAlign: "center", color: "#B14837", fontSize: 14 }}>
          {error}
        </div>
      )}

      {order && !loading && (
        <section style={{ padding: "20px 40px 80px", maxWidth: 1080, margin: "0 auto" }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.35 }}
            style={{
              background: "#FFFFFF",
              border: `1px solid ${BORDER_SOFT}`,
              borderRadius: 4,
              padding: "40px 40px 36px",
            }}
          >
            {/* Meta bar */}
            <div
              className="flex items-start flex-wrap"
              style={{
                gap: 40,
                paddingBottom: 28,
                borderBottom: `1px solid ${BORDER_SOFT}`,
                marginBottom: 30,
              }}
            >
              <div style={{ flex: "1 1 200px" }}>
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: "2px",
                    color: "rgba(84,84,84,0.55)",
                    marginBottom: 8,
                    textTransform: "uppercase",
                  }}
                >
                  Order number
                </div>
                <div
                  style={{
                    fontFamily: "'Instrument Serif', serif",
                    fontStyle: "italic",
                    fontSize: 34,
                    color: TEXT_COLOR,
                    letterSpacing: "-0.8px",
                    fontWeight: 400,
                    lineHeight: 1,
                  }}
                >
                  #{order.id}
                </div>
              </div>
              <div style={{ flex: "1 1 200px" }}>
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: "2px",
                    color: "rgba(84,84,84,0.55)",
                    marginBottom: 8,
                    textTransform: "uppercase",
                  }}
                >
                  Placed on
                </div>
                <div style={{ fontSize: 15, color: TEXT_COLOR, lineHeight: 1.4 }}>
                  {new Date(order.created_at).toLocaleString(undefined, {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </div>
              </div>
              <div style={{ flex: "1 1 200px" }}>
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: "2px",
                    color: "rgba(84,84,84,0.55)",
                    marginBottom: 8,
                    textTransform: "uppercase",
                  }}
                >
                  Status
                </div>
                <div
                  className="inline-flex items-center"
                  style={{
                    gap: 8,
                    padding: "5px 12px",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 500,
                    color: TEXT_COLOR,
                    letterSpacing: "1.5px",
                    border: `1px solid ${BORDER_MID}`,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 999,
                      background: "#7ea87e",
                      display: "inline-block",
                    }}
                  />
                  {(order.status ?? "paid").toUpperCase()}
                </div>
              </div>
            </div>

            {/* Column labels */}
            <div
              className="flex items-center"
              style={{
                padding: "0 0 14px",
                fontSize: 10,
                letterSpacing: "2px",
                color: "rgba(84,84,84,0.5)",
                textTransform: "uppercase",
                borderBottom: `1px solid ${BORDER_SOFT}`,
              }}
            >
              <div style={{ flex: 1 }}>Piece</div>
              <div style={{ width: 80, textAlign: "center" }}>Qty</div>
              <div style={{ width: 110, textAlign: "right" }}>Subtotal</div>
            </div>

            {order.order_items?.map((it) => (
              <div
                key={it.id}
                className="flex items-center"
                style={{
                  gap: 14,
                  padding: "18px 0",
                  borderBottom: `1px dashed ${BORDER_SOFT}`,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontFamily: "'Instrument Serif', serif",
                      fontStyle: "italic",
                      fontSize: 22,
                      color: TEXT_COLOR,
                      letterSpacing: "-0.5px",
                      fontWeight: 400,
                      lineHeight: 1.15,
                    }}
                  >
                    {it.product_name.split("—")[0]?.trim() || it.product_name}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "rgba(84,84,84,0.55)",
                      marginTop: 4,
                      letterSpacing: "1px",
                      textTransform: "uppercase",
                    }}
                  >
                    {formatPrice(Number(it.unit_price), currency)} EACH
                  </div>
                </div>
                <div
                  style={{
                    width: 80,
                    textAlign: "center",
                    fontFamily: "'Instrument Serif', serif",
                    fontStyle: "italic",
                    fontSize: 20,
                    color: TEXT_COLOR,
                    fontWeight: 400,
                  }}
                >
                  ×{it.quantity}
                </div>
                <div
                  style={{
                    width: 110,
                    textAlign: "right",
                    fontSize: 14,
                    color: TEXT_COLOR,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {formatPrice(Number(it.subtotal), currency)}
                </div>
              </div>
            ))}

            {/* Totals */}
            <div style={{ maxWidth: 360, marginLeft: "auto", marginTop: 26 }}>
              <SummaryRow label="Subtotal" value={formatPrice(subtotal, currency)} />
              <SummaryRow
                label="Shipping"
                value={shipping === 0 ? "Complimentary" : formatPrice(shipping, currency)}
                italic={shipping === 0}
              />
              <div style={{ height: 1, background: BORDER_SOFT, margin: "16px 0 14px" }} />
              <div className="flex items-baseline justify-between">
                <span style={{ fontSize: 12, letterSpacing: "2.5px", color: TEXT_COLOR }}>
                  TOTAL PAID
                </span>
                <span
                  style={{
                    fontFamily: "'Instrument Serif', serif",
                    fontStyle: "italic",
                    fontSize: 36,
                    color: TEXT_COLOR,
                    letterSpacing: "-1px",
                    fontWeight: 400,
                    lineHeight: 1,
                  }}
                >
                  {formatPrice(total, currency)}
                </span>
              </div>
            </div>

            {/* Recipient / delivery */}
            {order.recipient && (
              <>
                <div style={{ height: 1, background: BORDER_SOFT, margin: "34px 0 30px" }} />
                <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 40 }}>
                  <div>
                    <div
                      className="flex items-baseline"
                      style={{ gap: 12, marginBottom: 14 }}
                    >
                      <span
                        style={{
                          fontFamily: "'Instrument Serif', serif",
                          fontStyle: "italic",
                          fontSize: 18,
                          color: "rgba(84,84,84,0.5)",
                          fontWeight: 400,
                        }}
                      >
                        (a)
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          letterSpacing: "0.2px",
                          color: TEXT_COLOR,
                          textTransform: "uppercase",
                        }}
                      >
                        Shipping to
                      </span>
                    </div>
                    <div style={{ fontSize: 14, color: TEXT_COLOR, lineHeight: 1.7, marginLeft: 32 }}>
                      <span
                        style={{
                          fontFamily: "'Instrument Serif', serif",
                          fontStyle: "italic",
                          fontSize: 18,
                        }}
                      >
                        {order.recipient.name}
                      </span>
                      <br />
                      {order.recipient.address}
                      <br />
                      {order.recipient.city} {order.recipient.zip}
                      <br />
                      {order.recipient.country}
                    </div>
                  </div>
                  <div>
                    <div
                      className="flex items-baseline"
                      style={{ gap: 12, marginBottom: 14 }}
                    >
                      <span
                        style={{
                          fontFamily: "'Instrument Serif', serif",
                          fontStyle: "italic",
                          fontSize: 18,
                          color: "rgba(84,84,84,0.5)",
                          fontWeight: 400,
                        }}
                      >
                        (b)
                      </span>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          letterSpacing: "0.2px",
                          color: TEXT_COLOR,
                          textTransform: "uppercase",
                        }}
                      >
                        Delivery window
                      </span>
                    </div>
                    <div style={{ fontSize: 14, color: TEXT_COLOR, lineHeight: 1.7, marginLeft: 32 }}>
                      Estimated{" "}
                      <span
                        style={{
                          fontFamily: "'Instrument Serif', serif",
                          fontStyle: "italic",
                          fontSize: 16,
                        }}
                      >
                        5–8 business days
                      </span>
                      .
                      <br />A tracking link will follow by email.
                    </div>
                  </div>
                </div>
              </>
            )}
          </motion.div>

          {/* Actions */}
          <div
            className="flex justify-center flex-wrap"
            style={{ gap: 14, marginTop: 40 }}
          >
            <Link
              to="/catalog"
              className="cursor-pointer whitespace-nowrap transition-opacity hover:opacity-85"
              style={{
                background: "transparent",
                color: TEXT_COLOR,
                border: `1px solid ${TEXT_COLOR}`,
                borderRadius: 999,
                padding: "14px 28px",
                fontSize: 12,
                letterSpacing: "2px",
                textDecoration: "none",
              }}
            >
              CONTINUE SHOPPING
            </Link>
            <Link
              to="/"
              className="cursor-pointer whitespace-nowrap transition-opacity hover:opacity-85 inline-flex items-center"
              style={{
                background: TEXT_COLOR,
                color: "#FFFFFF",
                border: `1px solid ${TEXT_COLOR}`,
                borderRadius: 999,
                padding: "14px 28px",
                fontSize: 12,
                letterSpacing: "2px",
                textDecoration: "none",
                gap: 8,
              }}
            >
              BACK HOME
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-line" style={{ fontSize: 14 }}></i>
              </span>
            </Link>
          </div>
        </section>
      )}

      {/* Editorial signature footer */}
      <div
        style={{
          maxWidth: 1080,
          margin: "0 auto",
          padding: "0 40px 40px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          borderTop: `1px solid ${BORDER_SOFT}`,
          paddingTop: 28,
        }}
      >
        <span style={{ fontSize: 11, letterSpacing: "2.5px", color: "rgba(84,84,84,0.5)" }}>
          MAISON DES JOUETS · ATELIER
        </span>
        <span
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 22,
            color: "rgba(84,84,84,0.5)",
          }}
        >
          — made slowly, with love
        </span>
      </div>

      <ShopFooter />
    </main>
  );
}

function SummaryRow({
  label,
  value,
  italic,
}: {
  label: string;
  value: string;
  italic?: boolean;
}) {
  return (
    <div className="flex items-center justify-between" style={{ marginBottom: 10 }}>
      <span style={{ fontSize: 13, color: "rgba(84,84,84,0.75)" }}>{label}</span>
      <span
        style={{
          fontSize: italic ? 15 : 14,
          color: TEXT_COLOR,
          fontWeight: italic ? 400 : 500,
          fontFamily: italic ? "'Instrument Serif', serif" : "'Inter Tight', sans-serif",
          fontStyle: italic ? "italic" : "normal",
          letterSpacing: italic ? "-0.3px" : "0",
        }}
      >
        {value}
      </span>
    </div>
  );
}